import { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/router";
import { slugToPlatform, platformSlugs } from "@/config/platformSlugs";
import DownloaderPage from "@/components/DownloaderPage";
import React from "react";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import debugLogger from '@/lib/utils/debugLogger';

/**
 * Dynamic downloader page for each platform, with multilingual support.
 * Automatic SSR/SSG, SEO optimized, centralized paths, easy to maintain.
 */

interface Props {
  platform: string;
  translations: {
    platform: any;
    commonDownloader: any;
    common: any;
    features: any;
    howTo: any;
    faq: any;
    seo: any;
    platforms: any;
    'error-report': any;
  };
}

const PlatformDownloader: React.FC<Props> = ({ platform, translations }) => {
  // If needed, locale can be retrieved from useRouter for SEO, i18n...
  return <DownloaderPage platform={platform} translations={translations} />;
};

export default PlatformDownloader;

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  // Build all slugs for each locale (slugs don't change, only prefix /vi/)
  const paths =
    locales?.flatMap((locale) =>
      Object.values(platformSlugs).map((slug) => ({
        params: { slug },
        locale,
      }))
    ) || [];
  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const slug = params?.slug as string;
  const platform = slugToPlatform[slug] || null;
  if (!platform) return { notFound: true };
  
  // Load only the current platform's namespace and common
  const platformNamespaces = [
    `downloader/${platform}`,
    'downloader/common'
  ];
  
  // Define core namespaces
  const coreNamespaces = [
    'common',
    'howTo',
    'features',
    'faq',
    'header',
    'footer',
    'hero',
    'platforms',
    'seo',
    'about',
    'error-report'
  ];
  
  try {
    // Load all namespaces to ensure they're available during hydration
    const i18nResult = await serverSideTranslations(
      locale || 'en', 
      [...coreNamespaces, ...platformNamespaces]
    );

    // Extract translations from i18nResult._nextI18Next.initialI18nStore
    const lang = locale || 'en';
    const store = i18nResult._nextI18Next?.initialI18nStore?.[lang] || {};

    const translations = {
      platform: store[`downloader/${platform}`] || {},
      commonDownloader: store['downloader/common'] || {},
      common: store['common'] || {},
      features: store['features'] || {},
      howTo: store['howTo'] || {},
      faq: store['faq'] || {},
      seo: store['seo'] || {},
      platforms: store['platforms'] || {},
      'error-report': store['error-report'] || {},
    };

    return {
      props: {
        platform,
        translations,
        ...i18nResult,
      },
      // Use ISR with a reasonable revalidation period
      revalidate: 3600, // Revalidate every hour
    };
  } catch (error) {
    debugLogger.error(`Error loading translations for platform: ${platform}`, { 
      error: error instanceof Error ? error.message : 'Unknown error' 
    });
    
    // Fallback to core namespaces if there's an error
    return {
      props: {
        platform,
        ...(await serverSideTranslations(locale || 'en', coreNamespaces)),
      },
      revalidate: 600, // Retry sooner if there was an error
    };
  }
};
